import { Check } from 'lucide-react'

import { cn } from '@/lib/utils'

import type { WinnerImportStage } from './winner-import-progress'

const STEPS: Array<{ key: string; label: string; stages: WinnerImportStage[] }> = [
  { key: 'file', label: 'Fichier', stages: ['drop', 'stopped'] },
  { key: 'reading', label: 'Lecture', stages: ['reading'] },
  { key: 'review', label: 'Vérification', stages: ['review'] },
  { key: 'create', label: 'Création', stages: ['creating', 'done'] },
]

type Props = {
  stage: WinnerImportStage
  progress: number
  subtitle: string
}

/**
 * The bar that makes a long, careful job feel like it is moving: four named steps and one number
 * that only ever goes up.
 */
export function WinnerProgressHeader({ stage, progress, subtitle }: Props) {
  const current = STEPS.findIndex((s) => s.stages.includes(stage))

  return (
    <div className="border-b bg-background/95 px-6 py-4 backdrop-blur">
      <div className="mx-auto flex max-w-6xl flex-col gap-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Importer depuis Winner</h2>
            <p className="text-sm text-muted-foreground">{subtitle}</p>
          </div>
          <div className="text-right" data-testid="winner-import-progress-value" data-progress={progress}>
            <span className="text-2xl font-semibold tabular-nums">{progress}</span>
            <span className="text-sm text-muted-foreground"> % du dossier prêt</span>
          </div>
        </div>

        <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
          <div
            className={cn(
              'h-full rounded-full transition-[width] duration-700 ease-out',
              progress >= 100 ? 'bg-emerald-500' : 'bg-slate-900'
            )}
            style={{ width: `${progress}%` }}
          />
        </div>

        <ol className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
          {STEPS.map((step, index) => {
            const done = index < current || (stage === 'done' && index === current)
            const active = index === current && stage !== 'done'
            return (
              <li key={step.key} className="flex items-center gap-2">
                <span
                  className={cn(
                    'flex h-5 w-5 items-center justify-center rounded-full border text-[11px] font-medium',
                    done && 'border-emerald-500 bg-emerald-500 text-white',
                    active && 'border-slate-900 bg-slate-900 text-white',
                    !done && !active && 'border-slate-300 text-slate-400'
                  )}
                >
                  {done ? <Check className="h-3 w-3" /> : index + 1}
                </span>
                <span className={cn(active ? 'font-medium text-foreground' : 'text-muted-foreground')}>{step.label}</span>
              </li>
            )
          })}
        </ol>
      </div>
    </div>
  )
}
